import { useCallback, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import { Text } from '@antimatterhq/ui/components/typography';
import { Button } from '@antimatterhq/ui/components/button';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Label } from '@antimatterhq/ui/components/label';
import { ButtonGroup } from '@antimatterhq/ui/components/buttonGroup';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FlagSet } from '@antimatterhq/ui/utils/flags';
import { JSONExtraction } from '../jsonExtract/JSONExtract';
import { UnreferencedFields } from '../unreferencedFields/UnreferencedFields';
import { CustomNotebookFunction } from '../customNotebookFunction/CustomNotebookFunction';
import { DatasourceTemporaryFields } from '../temporaryFields/DatasourceTemporaryFields';
import { FieldSpecTable } from '@/components/table/FieldSpecTable/FieldSpecTable';
import {
  addFieldSchemaValue,
  deleteFieldSchemaValue,
  generateNotebookFunctionName,
  generateNotebookName,
  getCopyableFields,
  getFieldSchemaValue,
  getFieldTableRows,
  getTakenNames,
  mergeFieldSpecs,
  setCustomFunctionRequest,
  setFieldSchemaValues,
  updateFieldSchemaValue,
} from '@/lib/datasource';
import CustomFieldDialog from '@/components/dialog/customField/CustomField';
import {
  DataSourceTablesSchemaValues,
  FieldSpecSchemaValues,
  ModifyTableSchema,
} from '@/zod/schemas/dataSource/Common';
import { useConfigDataSource } from '@/contexts/ConfigDataSourceContext';
import { getFormikNames } from '@/utils/util';
import { FormDatabricksPathAndTableValidatedInput } from '@/components/form/input/FormDatabricksPathAndTableValidatedInput';
import { FormDropdown, FormError } from '@/components/form/form';
import { FormValues } from '@/components/form/values/FormValues';
import { useCreateNotebookFunction } from '@/hooks/dbui/useCreateNotebookFunction';
import { optCustomTooltip, optDefaultTooltip } from '@/strings/StringsTooltips';
import { filterNulls } from '@/utils/filterNulls';
import { DataSourceFilters } from '@/components/dataSources/filters/DataSourceFilters';
import { useCreateNotebook } from '@/hooks/dbui/useCreateNotebook';
import {
  FormAccordion,
  FormAccordionContent,
  FormAccordionItem,
  FormAccordionTrigger,
} from '@/components/form/accordion/FormAccordion';
import { Datasource_FieldSpec, Datasource_PresetItem } from '@/types/api';

export type ModifyTableProps = {
  index: number;
  currentTable: 'gold' | 'silver';
  previousTable: 'silver' | 'bronze';
  table: NonNullable<DataSourceTablesSchemaValues['modifyTables']>[number];
  sourceTableNames?: string[];
  enableClustering?: boolean;
  presetTables?: Datasource_PresetItem[];
};

type ModifyTableKeys = keyof typeof ModifyTableSchema.shape;

export const ModifyTable = ({
  index,
  currentTable,
  previousTable,
  table,
  sourceTableNames,
  presetTables,
  enableClustering = false,
}: ModifyTableProps) => {
  const formik = useFormikContext<DataSourceTablesSchemaValues>();
  const [customField, setCustomField] = useState<
    FieldSpecSchemaValues | undefined
  >(undefined);
  const { dataSource } = useConfigDataSource();
  const { enableJsonExtraction, enableTemporaryFields } = useFlags<FlagSet>();

  const { setFieldValue, handleChange, handleBlur, isSubmitting } = formik;
  const prefix = `modifyTables.${index}.`;

  const presetTable = useMemo(() => {
    return presetTables?.find(({ name }) => name === table.name);
  }, [presetTables, table]);

  const {
    mutateAsync: createCustomNotebook,
    isPending: isCreatingCustomNotebook,
  } = useCreateNotebook();
  const {
    mutateAsync: createNotebookFunction,
    isPending: isCreatingNotebookFunction,
  } = useCreateNotebookFunction();

  const names = useMemo(
    () => getFormikNames<ModifyTableKeys>(ModifyTableSchema.shape, prefix),
    [prefix],
  );

  const presetFields = useMemo(() => {
    return filterNulls(presetTable?.fields);
  }, [presetTable]);

  const fieldSpecData = useMemo(() => {
    return getFieldTableRows(
      presetFields,
      filterNulls(table.addFields),
      filterNulls(table.omitFields),
    );
  }, [table.addFields, table.omitFields, presetFields]);

  const takenNames = useMemo(
    () => getTakenNames(fieldSpecData),
    [fieldSpecData],
  );

  const copyableFields = useMemo(
    () => getCopyableFields(fieldSpecData),
    [fieldSpecData],
  );

  const mergedFields = useMemo(() => {
    return mergeFieldSpecs(
      filterNulls(presetTable?.fields),
      filterNulls(setFieldSchemaValues(table.addFields)),
      filterNulls(table.omitFields),
    );
  }, [presetTable, table.addFields, table.omitFields]);

  const onDeleteField = useCallback(
    (field: FieldSpecSchemaValues) => {
      setFieldValue(
        names.addFields,
        deleteFieldSchemaValue(field.name!, table.addFields),
      );
    },
    [table.addFields, setFieldValue, names],
  );

  const addField = useCallback(
    (fieldSpec: Datasource_FieldSpec) => {
      setFieldValue(
        names.addFields,
        addFieldSchemaValue(fieldSpec, table.addFields),
      );
      // If adding a field with the same name as a preset field, add it to omitFields
      if (
        fieldSpec.name &&
        presetFields.some((field) => field.name === fieldSpec.name)
      ) {
        const currentOmitFields = [...(table.omitFields || [])];
        if (!currentOmitFields.includes(fieldSpec.name)) {
          setFieldValue(names.omitFields, [
            ...currentOmitFields,
            fieldSpec.name,
          ]);
        }
      }
    },
    [
      setFieldValue,
      names.addFields,
      names.omitFields,
      table.addFields,
      table.omitFields,
      presetFields,
    ],
  );

  const updateField = useCallback(
    (fieldSpec: Datasource_FieldSpec) => {
      setFieldValue(
        names.addFields,
        updateFieldSchemaValue(fieldSpec, customField, table.addFields),
      );
    },
    [table.addFields, setFieldValue, customField, names],
  );

  const onEditField = useCallback(
    (source: {
      preset?: Datasource_FieldSpec;
      custom?: FieldSpecSchemaValues;
    }) => {
      let custom = source.custom;
      if (!custom) {
        custom = getFieldSchemaValue(source.preset);
      }
      setCustomField(custom);
    },
    [],
  );

  const onToggleField = useCallback(
    (field: Datasource_FieldSpec, enabled: boolean) => {
      if (!field.name) return;

      const value = [...(table.omitFields || [])].filter(
        (q) => q !== field.name,
      );
      if (!enabled) {
        value.push(field.name);
      }
      setFieldValue(names.omitFields, value);
    },
    [names.omitFields, setFieldValue, table.omitFields],
  );

  const onRevertField = useCallback(
    (source: {
      preset: Datasource_FieldSpec;
      custom: FieldSpecSchemaValues;
    }) => {
      // Remove the custom field from addFields
      const updatedAddFields = (table.addFields || []).filter(
        (field) => field !== source.custom,
      );
      setFieldValue(names.addFields, updatedAddFields);

      // Remove the preset name from omitFields
      const updatedOmitFields = (table.omitFields || []).filter(
        (field) => field !== source.preset.name,
      );
      setFieldValue(names.omitFields, updatedOmitFields);
    },
    [
      names.addFields,
      names.omitFields,
      table.addFields,
      table.omitFields,
      setFieldValue,
    ],
  );

  const onCreateCustomNotebookFunction = useCallback(async () => {
    let customNotebookPath = formik.values.customNotebookPath;
    if (!customNotebookPath) {
      const path = generateNotebookName(dataSource.metadata?.name || '');
      await createCustomNotebook(
        { path, templateType: 'datasource' },
        {
          onSuccess: async ({ data }) => {
            customNotebookPath = data.notebookPath;
            setFieldValue('customNotebookPath', customNotebookPath);
          },
          toasts: {
            success: 'Notebook created!',
            error: 'Failed to create notebook',
          },
        },
      );
    }
    const name = generateNotebookFunctionName(
      dataSource.metadata?.name || '',
      currentTable,
    );
    const request = setCustomFunctionRequest(
      customNotebookPath || '',
      name,
      table,
      mergedFields,
    );
    createNotebookFunction(request, {
      onSuccess: async ({ data }) => {
        setFieldValue(names.customFunction, name);
        setFieldValue(names.tableMode, 'custom');
        window.open(data.notebookUrl, '_blank', 'noopener,noreferrer');
      },
      toasts: {
        success: 'Notebook function created',
        error: 'Failed to create notebook function',
      },
    });
  }, [
    createNotebookFunction,
    dataSource,
    table,
    setFieldValue,
    mergedFields,
    names,
    currentTable,
    createCustomNotebook,
    formik.values.customNotebookPath,
  ]);

  const disabled = isSubmitting || table.tableMode === 'disable';
  const enableUtils = currentTable === 'silver';

  return (
    <>
      <Spacer size="lg" />
      <Label htmlFor={names.name} id={`${names.name}-label`} required>
        Table name
      </Label>
      <FormDatabricksPathAndTableValidatedInput
        name={names.name}
        id={names.name}
        disabled
        placeholder="Table name"
        constraint="mustExistReadWriteOrBeCreatable"
        validationType="table"
        tableSchema={currentTable === 'silver' ? 'silverSchema' : 'goldSchema'}
        required
      />
      <FormError name={names.name} />
      <Spacer size="lg" />
      <ButtonGroup
        options={[
          { label: 'Configure preset & fields', value: 'configure' },
          { label: 'Custom function', value: 'custom' },
          { label: 'Disable table', value: 'disable' },
        ]}
        value={table.tableMode}
        onChange={(value) => {
          setFieldValue(names.tableMode, value);
        }}
        disabled={isSubmitting}
        id={`modify-table-mode-${index}`}
        data-testid={`modify-table-mode-${index}-button-group`}
      />
      {!!sourceTableNames && (
        <>
          <Spacer size="lg" />

          <Label
            htmlFor={`${prefix}sourceTable`}
            required
            id={`${prefix}sourceTable-label`}
          >
            Source table
          </Label>
          <FormDropdown
            placeholder="Source table"
            options={sourceTableNames}
            value={table.sourceTable}
            onChange={handleChange}
            onBlur={handleBlur}
            name={`${prefix}sourceTable`}
            disabled={disabled}
            aria-labelledby={`${prefix}sourceTable-label`}
          />
          <FormError name={`${prefix}sourceTable`} />
        </>
      )}
      {table.tableMode === 'custom' && (
        <>
          <Spacer size="lg" />
          <CustomNotebookFunction
            values={table}
            prefix={prefix}
            currentTable={currentTable}
            notebookPath={formik.values.customNotebookPath}
          />
        </>
      )}
      {table.tableMode !== 'custom' && (
        <>
          <Spacer size="lg" />

          <Text size="lg" className="block" bold>
            Current field list
          </Text>
          <FieldSpecTable
            data={fieldSpecData}
            onDelete={onDeleteField}
            onEdit={onEditField}
            onToggle={onToggleField}
            disabled={disabled}
            onRevert={onRevertField}
            data-testid={`modify-table-field-spec-table-${index}`}
          />
          <div>
            <Spacer size="lg" />
            <Button
              leftIcon="radix-icons:plus"
              variant="default"
              className="w-fit"
              onClick={() => setCustomField({})}
              type="button"
              disabled={disabled}
              data-testid={`modify-table-add-field-button-${index}`}
            >
              Add a Field
            </Button>
          </div>
          {enableClustering && (
            <>
              <Spacer size="xl" />

              <ButtonGroup
                options={[
                  {
                    label: 'Default clustering',
                    value: 'default',
                    info: { content: optDefaultTooltip },
                  },
                  {
                    label: 'Custom clustering',
                    value: 'custom',
                    info: { content: optCustomTooltip },
                  },
                ]}
                value={table.enableOverrideLiquidColumns ? 'custom' : 'default'}
                onChange={(value) => {
                  setFieldValue(
                    names.enableOverrideLiquidColumns,
                    value === 'custom',
                  );
                }}
                disabled={isSubmitting}
                id={`modify-table-clustering-${index}`}
                data-testid={`modify-table-clustering-${index}-button-group`}
              />

              {table.enableOverrideLiquidColumns && (
                <>
                  <Spacer size="lg" />

                  <Label
                    htmlFor={`${prefix}overrideLiquidColumns`}
                    id={`${prefix}overrideLiquidColumns-label`}
                  >
                    Liquid clustering columns
                  </Label>
                  <FormValues
                    values={table.overrideLiquidColumns}
                    onChange={handleChange}
                    name={`${prefix}overrideLiquidColumns`}
                    onBlur={handleBlur}
                    disabled={disabled}
                    aria-labelledby={`${prefix}overrideLiquidColumns-label`}
                    data-testid={`modify-table-clustering-${index}-values`}
                  />
                </>
              )}
            </>
          )}

          <Spacer size="lg" />

          <FormAccordion type="multiple">
            <FormAccordionItem value="filters">
              <FormAccordionTrigger
                data-testid={`modify-table-filters-accordion-${index}-filters`}
              >
                <Text size="lg">Filters</Text>
              </FormAccordionTrigger>
              <FormAccordionContent>
                <DataSourceFilters
                  filters={table}
                  prefix={prefix}
                  disabled={disabled}
                />
              </FormAccordionContent>
            </FormAccordionItem>

            {enableUtils && (
              <FormAccordionItem value="preserve">
                <FormAccordionTrigger
                  data-testid={`modify-table-preserve-accordion-${index}-preserve`}
                >
                  <Text size="lg">Preserve unreferenced fields</Text>
                </FormAccordionTrigger>
                <FormAccordionContent className="-m-2 p-2">
                  <UnreferencedFields
                    unreferencedFields={table}
                    prefix={prefix}
                    disabled={disabled}
                  />
                </FormAccordionContent>
              </FormAccordionItem>
            )}

            {enableJsonExtraction && enableUtils && (
              <FormAccordionItem value="json">
                <FormAccordionTrigger
                  data-testid={`modify-table-json-accordion-${index}-json`}
                >
                  <Text size="lg">JSON extraction</Text>
                </FormAccordionTrigger>
                <FormAccordionContent className="-m-2 p-2">
                  <JSONExtraction
                    jsonExtract={table.jsonExtract}
                    prefix={prefix}
                    disabled={disabled}
                  />
                </FormAccordionContent>
              </FormAccordionItem>
            )}

            {enableTemporaryFields && enableUtils && (
              <FormAccordionItem value="temporaryFields">
                <FormAccordionTrigger
                  data-testid={`modify-table-temporary-fields-accordion-${index}-temporaryFields`}
                >
                  <Text size="lg">Temporary fields</Text>
                </FormAccordionTrigger>
                <FormAccordionContent className="-m-2 p-2">
                  <DatasourceTemporaryFields
                    values={table}
                    prefix={prefix}
                    disabled={disabled}
                    currentTable={currentTable}
                    previousTable={previousTable}
                  />
                </FormAccordionContent>
              </FormAccordionItem>
            )}
          </FormAccordion>

          <Spacer size="2xl" />

          <Button
            variant="default"
            onClick={() => onCreateCustomNotebookFunction()}
            disabled={
              disabled || isCreatingNotebookFunction || isCreatingCustomNotebook
            }
            leftIcon="lucide:notebook"
            loading={isCreatingNotebookFunction || isCreatingCustomNotebook}
            data-testid={`modify-table-create-custom-function-button-${index}`}
          >
            Convert to custom function
          </Button>

          {customField && (
            <CustomFieldDialog
              currentTable={currentTable}
              currentTableFields={copyableFields.filter(
                (name) => name !== customField.name,
              )}
              previousTable={previousTable}
              onSubmit={(fieldSpec: Datasource_FieldSpec) => {
                if (
                  !customField.name ||
                  !table.addFields.some((q) => q.name === customField.name)
                ) {
                  addField(fieldSpec);
                } else {
                  updateField(fieldSpec);
                }
              }}
              fieldSpec={customField}
              open={true}
              onClose={() => setCustomField(undefined)}
              takenNames={takenNames.filter(
                (name) => name !== customField.name,
              )}
              disableName={
                !!customField.name &&
                presetFields.some((q) => q.name === customField.name)
              }
            />
          )}
        </>
      )}
    </>
  );
};
